import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  ChevronDown,
  Loader2,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import { supabase } from '@/lib/customSupabaseClient';
import {
  checkStatusVariant,
  formatCheckValue,
  formatCoverage,
  formatDurationSeconds,
  formatEvidence,
  formatSignatureVerdict,
  formatThreshold,
  humanizeEnum,
  humanizeRuleKey,
  statusBadgeVariant,
  truncateFingerprint,
} from '@/lib/runningSignature';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const SIGNATURE_SELECT = [
  'status',
  'primary_rule',
  'analyzer_version',
  'config_fingerprint',
  'run_classification',
  'run_classification_source',
  'device_tier',
  'checks',
  'available_checks',
  'skipped_checks',
  'evidence',
  'muscle_context',
  'training_focus_tags',
  'available_metrics',
  'missing_metrics',
  'clean_window_seconds',
  'clean_window_coverage',
  'aggregate_metrics',
  'status_reason',
  'analyzed_at',
  'narrative_key',
].join(', ');

function ClassificationPill({ label, value }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <Badge variant="outline" className="w-fit font-normal">
        {humanizeEnum(value)}
      </Badge>
    </div>
  );
}

function MetricList({ title, items }) {
  if (!items?.length) return null;
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground mb-1">{title}</p>
      <div className="flex flex-wrap gap-1.5">
        {items.map((item) => (
          <Badge key={item} variant="secondary" className="font-normal">
            {item}
          </Badge>
        ))}
      </div>
    </div>
  );
}

function RunningSignatureCard({ userId, garminActivityId, activityDbId, onAnalyzed }) {
  const [signature, setSignature] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState(null);

  const fetchSignature = useCallback(async () => {
    if (!userId || !garminActivityId) return;
    setLoading(true);
    setFetchError(null);

    const { data, error } = await supabase
      .from('garmin_activity_signatures')
      .select(SIGNATURE_SELECT)
      .eq('user_id', userId)
      .eq('garmin_activity_id', garminActivityId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching running signature:', error);
      setFetchError(error.message);
      setSignature(null);
    } else {
      setSignature(data);
    }
    setLoading(false);
  }, [userId, garminActivityId]);

  useEffect(() => {
    fetchSignature();
  }, [fetchSignature]);

  const handleAnalyze = useCallback(async () => {
    if (!activityDbId || analyzing) return;
    setAnalyzing(true);
    setAnalyzeError(null);

    try {
      const { data, error } = await supabase.functions.invoke(
        'garmin-running-signature-analyze',
        { body: { mode: 'single', activityId: activityDbId } }
      );
      if (error) {
        const body = data ?? error?.context?.json ?? null;
        const reason = body?.error || body?.reason || error.message;
        throw new Error(reason);
      }
      if (data?.status === 'failed') {
        throw new Error(data.reason || 'Analysis failed');
      }
      await fetchSignature();
      onAnalyzed?.();
    } catch (err) {
      setAnalyzeError(err.message || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  }, [activityDbId, analyzing, fetchSignature, onAnalyzed]);

  const classification = signature?.run_classification || {};
  const checks = Array.isArray(signature?.checks) ? signature.checks : [];
  const primaryCheck = useMemo(
    () => checks.find((check) => check.rule_key === signature?.primary_rule),
    [checks, signature?.primary_rule]
  );
  const leadEvidence = useMemo(
    () => formatEvidence(primaryCheck?.evidence),
    [primaryCheck]
  );

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Running Mechanics
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Running Mechanics
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Tiered running signature analysis (coach view)
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleAnalyze}
            disabled={analyzing || !activityDbId}
          >
            {analyzing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            <span className="ml-1.5">
              {signature ? 'Re-analyze' : 'Analyze now'}
            </span>
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {fetchError && (
          <p className="text-sm text-destructive">
            Could not load running signature: {fetchError}
          </p>
        )}
        {analyzeError && (
          <p className="text-sm text-destructive">Analysis error: {analyzeError}</p>
        )}

        {!signature && !fetchError && (
          <div className="rounded-lg border border-dashed p-6 text-center space-y-3">
            <Sparkles className="h-8 w-8 mx-auto text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">
              This run has not been analyzed yet. Trigger analysis to generate a
              running mechanics verdict.
            </p>
            <Button size="sm" onClick={handleAnalyze} disabled={analyzing}>
              {analyzing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Analyzing…
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Analyze now
                </>
              )}
            </Button>
          </div>
        )}

        {signature && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={statusBadgeVariant(signature.status, signature.primary_rule)}>
                {signature.status.replace(/_/g, ' ')}
              </Badge>
              {signature.device_tier != null && (
                <Badge variant="outline">Tier {signature.device_tier}</Badge>
              )}
              {signature.analyzer_version && (
                <Badge variant="outline">Analyzer v{signature.analyzer_version}</Badge>
              )}
              {signature.run_classification_source && (
                <Badge variant="outline" className="font-normal capitalize">
                  {signature.run_classification_source.replace(/_/g, ' ')}
                </Badge>
              )}
            </div>

            <div className="rounded-lg bg-muted/40 p-4 space-y-2">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Verdict
              </p>
              <p className="text-lg font-semibold">
                {formatSignatureVerdict(signature)}
              </p>
              {signature.primary_rule && (
                <p className="text-sm text-muted-foreground">
                  Lead check:{' '}
                  <span className="font-medium text-foreground">
                    {humanizeRuleKey(signature.primary_rule)}
                  </span>
                </p>
              )}
              {signature.status_reason && (
                <p className="text-xs text-muted-foreground">
                  Reason: {signature.status_reason.replace(/_/g, ' ')}
                </p>
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <ClassificationPill label="Effort" value={classification.effort} />
              <ClassificationPill label="Structure" value={classification.structure} />
              <ClassificationPill label="Terrain" value={classification.terrain} />
            </div>

            {primaryCheck && (
              <div className="rounded-lg border p-4 space-y-3">
                <p className="text-sm font-medium">Lead check evidence</p>
                <div className="grid gap-2 sm:grid-cols-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Value: </span>
                    <span className="font-medium">
                      {formatCheckValue(primaryCheck.value)}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Threshold: </span>
                    <span>{formatThreshold(primaryCheck.threshold)}</span>
                  </div>
                </div>
                {leadEvidence.length > 0 && (
                  <dl className="grid gap-2 sm:grid-cols-2 text-xs">
                    {leadEvidence.map(([key, val]) => (
                      <div key={key} className="rounded bg-muted/30 px-2 py-1.5">
                        <dt className="text-muted-foreground">
                          {key.replace(/_/g, ' ')}
                        </dt>
                        <dd className="font-medium mt-0.5">
                          {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                )}
              </div>
            )}

            {signature.muscle_context && (
              <Accordion type="single" collapsible defaultValue="muscle">
                <AccordionItem value="muscle" className="border rounded-lg px-4">
                  <AccordionTrigger className="text-sm font-medium hover:no-underline py-3">
                    Muscle &amp; training context
                    <ChevronDown className="h-4 w-4 shrink-0 lucide-chevron-down transition-transform duration-200" />
                  </AccordionTrigger>
                  <AccordionContent className="pb-4 space-y-4">
                    <MetricList
                      title="Primary tissues"
                      items={signature.muscle_context.primary_tissues}
                    />
                    <MetricList
                      title="Secondary tissues"
                      items={signature.muscle_context.secondary_tissues}
                    />
                    {signature.muscle_context.loading_pattern && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">
                          Loading pattern
                        </p>
                        <p className="text-sm">{signature.muscle_context.loading_pattern}</p>
                      </div>
                    )}
                    {signature.muscle_context.fatigue_domain && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">
                          Fatigue domain
                        </p>
                        <p className="text-sm capitalize">
                          {signature.muscle_context.fatigue_domain}
                        </p>
                      </div>
                    )}
                    <MetricList
                      title="Training focus tags"
                      items={signature.training_focus_tags}
                    />
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Clean window</p>
                <p className="font-medium">
                  {formatDurationSeconds(signature.clean_window_seconds)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Window coverage</p>
                <p className="font-medium">
                  {formatCoverage(signature.clean_window_coverage)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Available checks</p>
                <p className="font-medium">{signature.available_checks?.length ?? 0}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Skipped checks</p>
                <p className="font-medium">{signature.skipped_checks?.length ?? 0}</p>
              </div>
            </div>

            <MetricList title="Available metrics" items={signature.available_metrics} />
            {signature.missing_metrics?.length > 0 && (
              <MetricList title="Missing metrics" items={signature.missing_metrics} />
            )}

            {checks.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">All checks</p>
                <div className="rounded-lg border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Check</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Value</TableHead>
                        <TableHead>Skip reason</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {checks.map((check) => (
                        <TableRow key={check.rule_key}>
                          <TableCell className="font-medium">
                            {humanizeRuleKey(check.rule_key)}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={checkStatusVariant(check.status)}
                              className="font-normal capitalize"
                            >
                              {check.status}
                            </Badge>
                          </TableCell>
                          <TableCell>{formatCheckValue(check.value)}</TableCell>
                          <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">
                            {check.skip_reason?.replace(/_/g, ' ') || '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground pt-2 border-t">
              {signature.analyzed_at && (
                <span>
                  Analyzed{' '}
                  {new Date(signature.analyzed_at).toLocaleString(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                </span>
              )}
              {signature.config_fingerprint && (
                <span title={signature.config_fingerprint}>
                  Config {truncateFingerprint(signature.config_fingerprint)}
                </span>
              )}
              {signature.narrative_key && (
                <span className="truncate max-w-full">{signature.narrative_key}</span>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default RunningSignatureCard;
